// useInitializeViewerState — extrahiert aus
// app/src/components/editor/Viewer/useInitializeViewerState.ts
// (Plan A Mapping-Audit 2026-05-22).
//
// Abstrahiert app/-Coupling:
//   - `env.imgproxyUrl` → `options.imgproxyUrl` Parameter
//   - `track.storyNavigation` (Google Analytics) → `options.onEvent` Callback
//   - `useResizeObserver` → lokaler Import (gleicher Package-Pfad)
//
// Renderer-Lifecycle bleibt 1:1 wie in app/: bei Mount Renderer + Store +
// PlayablePresentation initialisieren, start() läuft, aber Slide-Navigation
// erst wenn scrollTop > 0 (was useBlockVertScroll bis User-Click verhindert).

import type { Dispatch, RefObject, SetStateAction } from "react";
import { useCallback, useEffect, useRef } from "react";
import {
  Renderer,
  VisualStoryStore,
  RendererStore,
  PlayablePresentation,
  WebGLAssets,
  createImageOptimizer,
} from "@scrolly/renderer";
import type {
  RendererProps,
  VisualStoryEventHandler,
} from "@scrolly/renderer";
import type { CustomFonts, PostJson } from "@scrolly/post-types";
import useResizeObserver from "./useResizeObserver";

export interface UseInitializeViewerStateOptions {
  /**
   * imgproxy base URL für Image-Optimization. Leer-String oder undefined
   * fällt auf raw Asset-URL zurück. In app/ aus `env.imgproxyUrl`, in
   * public/ aus `import.meta.env.VITE_IMGPROXY_URL`.
   */
  imgproxyUrl?: string;
  /**
   * Analytics-Callback für Renderer-Events (heute nur "storyNavigation").
   * Wenn undefined: No-Op. app/ bridged das auf Google Analytics dataLayer
   * via `track.storyNavigation`. public/ konsumiert (heute) nicht, kann
   * in Plan-2 eigene Tracking-Pipeline einhaken.
   */
  onEvent?: VisualStoryEventHandler;
  /**
   * Scroll-Source-Polarität (Plan-2 iOS-Fix). DEFAULT "container" (heute).
   * Nur public/-Immersive-Full-Page-Reader → "window" (window.scrollY).
   */
  scrollSource?: "container" | "window";
  /** Editor template preview — show full bloom quote text while editing. */
  editorPreview?: boolean;
  /**
   * Pro-Editor-Canvas? Bestimmt das INSTANZ-editorMode des VisualStoryStore
   * (Timer-Umleitung in Jans Playhead, kein Start-Scroll). Muss pro Instanz
   * sein: Im Pro-Editor laufen Editor-Canvas UND Vorschau-Dialog gleichzeitig
   * — die globale WebGLAssets.editorMode-Flagge sperrte im Dialog den Start
   * (Jans Fund 08.08.2026). undefined = globale Flagge (Alt-Verhalten).
   */
  proEditor?: boolean;
  /** Editor: hide Sentence-Bloom SVG while the Titel & Hintergrund panel is active. */
  sentenceBloomCoverPreview?: boolean;
  /** Obergrenze für Bild-Texturbreiten (Kachel-Vorschauen) — Begründung an
   *  RendererProps.maxTextureWidth im Renderer-Paket. */
  maxTextureWidth?: number;
}

interface ViewerState {
  onResize: () => void;
  renderer: RefObject<Renderer | null>;
  visualStoryStore: RefObject<VisualStoryStore | null>;
  mainContainerRef: RefObject<HTMLDivElement | null>;
  canvasContainerRef: RefObject<HTMLDivElement | null>;
  htmlRendererRef: RefObject<HTMLDivElement | null>;
  viewerRootContainerRef: RefObject<HTMLElement | null>;
}

/**
 * Aufbau des Render-Baums als Text: Ebenen-Ids mit Verschachtelung und
 * Reihenfolge. Ändert er sich, muss der Renderer den Baum neu bauen; bei
 * reinen Wertänderungen (Position, Farbe …) bleibt er gleich.
 */
function baumAufbau(pp: PlayablePresentation): string {
  type Knoten = { id?: string; layers?: unknown };
  const text = (liste: unknown): string =>
    Array.isArray(liste)
      ? (liste as Knoten[])
          .map((l) => `${l?.id}${Array.isArray(l?.layers) && l.layers.length ? `[${text(l.layers)}]` : ""}`)
          .join(",")
      : "";
  return text((pp.presentationRender as unknown as { groupTree?: Knoten })?.groupTree?.layers);
}

export default function useInitializeViewerState(
  id: string | number,
  post: PostJson,
  setProgress: Dispatch<SetStateAction<number>>,
  setCurrentSlideIndex: Dispatch<SetStateAction<number>>,
  setStarted: Dispatch<SetStateAction<boolean>>,
  viewerRootContainerRef: RefObject<HTMLElement | null>,
  heightRef: RefObject<HTMLElement | null>,
  fontFamilies: CustomFonts,
  options?: UseInitializeViewerStateOptions,
): ViewerState {
  const renderer = useRef<Renderer>(null);
  const initializedId = useRef<string | number>(null);
  const visualStoryStore = useRef<VisualStoryStore>(null);
  const playablePresentationRef = useRef<PlayablePresentation | null>(null);
  // Tracks the `post` the render presentation was last built from, so an EDITOR
  // content change (a new post identity) rebuilds the scene. Reader posts are
  // static → this never fires there.
  const lastAppliedPostRef = useRef<PostJson | null>(null);
  // Aufbau des Render-Baums beim letzten Neubau (s. baumAufbau)
  const baumAufbauRef = useRef<string | null>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const htmlRendererRef = useRef<HTMLDivElement>(null);

  const imgproxyUrl = options?.imgproxyUrl ?? "";

  // Stable-ref pattern: consumer-supplied onEvent darf zwischen Renders
  // wechseln (z.B. inline-arrow), aber unsere useEffect-Dependency soll
  // dadurch nicht das Setup neu triggern. Wir lesen aus ref bei jedem
  // Event-Fire.
  const onEventRef = useRef(options?.onEvent);
  useEffect(() => {
    onEventRef.current = options?.onEvent;
  });

  // Editor panel toggles this each render — must not be captured once in init.
  const sentenceBloomCoverPreviewRef = useRef(
    options?.sentenceBloomCoverPreview ?? false,
  );
  useEffect(() => {
    sentenceBloomCoverPreviewRef.current =
      options?.sentenceBloomCoverPreview ?? false;
  });

  const isCoverActive = useCallback((): boolean => {
    if (sentenceBloomCoverPreviewRef.current) return true;
    const store = visualStoryStore.current;
    if (!store) return true;
    if (store.innerHeight <= 0) return true;
    return store.beforeStory;
  }, []);

  const bridgedEvent: VisualStoryEventHandler = useCallback(
    (name, payload) => {
      onEventRef.current?.(name, payload);
    },
    [],
  );

  const stopCallback = useCallback(() => {
    // No-op placeholder — Renderer expects a stop()-callback but the actual
    // teardown happens via stopViewer() in cleanup.
  }, []);

  // Read visualStoryStore.current at CALL time, not closure time. Previously
  // captured at render time (null on first render), causing onResize() to
  // silently no-op during initialization and viewport toggle.
  const onResize = useCallback(() => {
    const storyStore = visualStoryStore.current;
    if (storyStore && renderer.current) {
      storyStore.regenerateBoundsCache();
      const rendererStore = renderer.current.props.renderer;
      const height = storyStore.innerHeight;
      const width = storyStore.innerWidth;
      rendererStore.setHeight(height);
      rendererStore.setWidth(width);
      rendererStore.setBounds(height, width, 0, 0);
      storyStore.regenerateSlidePositionsCache();
    }
  }, []);

  const stopViewer = useCallback(() => {
    playablePresentationRef.current?.slidePlayer?.stopLoop();
    playablePresentationRef.current?.stop();
    visualStoryStore.current?.stop();

    visualStoryStore.current = null;
    playablePresentationRef.current = null;

    if (renderer.current) {
      renderer.current.stop();
      renderer.current.dispose();
      renderer.current.webGLRenderer?.domElement.remove();
      renderer.current = null;
    }

    WebGLAssets.layerIndex = 0;
    WebGLAssets.allLayersSet = false;
    WebGLAssets.setImagesOnStart = false;
    WebGLAssets.setVideosOnStart = false;
    WebGLAssets.setGpuTextOnStart = false;
  }, []);

  useEffect(() => {
    return () => {
      stopViewer();
    };
  }, [stopViewer]);

  useEffect(() => {
    const imageOptimizer = createImageOptimizer(imgproxyUrl);

    if (
      renderer.current === null &&
      canvasContainerRef.current &&
      htmlRendererRef.current &&
      mainContainerRef.current &&
      viewerRootContainerRef.current &&
      heightRef.current
    ) {
      const rendererStore = new RendererStore((newProgress) => {
        setProgress(newProgress);
      });
      const playablePresentation = new PlayablePresentation(
        post.data.data,
        rendererStore,
      );
      playablePresentation.slidePlayer.setOnSlideUpdate(setCurrentSlideIndex);
      const rendererProps: RendererProps = {
        isProduction: true,
        stop: stopCallback,
        start: (animate) => {
          playablePresentation.slidePlayer.startLoop(animate);
        },
        renderer: playablePresentation.renderer,
        slidePlayer: playablePresentation.slidePlayer,
        presentation: playablePresentation.presentationRender,
        isCoverActive,
        maxTextureWidth: options?.maxTextureWidth,
        editorMode: options?.proEditor,
      };

      if (visualStoryStore.current === null) {
        visualStoryStore.current = new VisualStoryStore(
          viewerRootContainerRef.current,
          heightRef.current,
          bridgedEvent,
          options?.scrollSource ?? "container",
          options?.proEditor,
          // Template-Editor-Vorschau: kein Per-Breite-Hoehen-Cache (Ratio-Wechsel).
          options?.editorPreview,
        );
      }
      visualStoryStore.current.setPost(playablePresentation);
      renderer.current = new Renderer(
        canvasContainerRef.current,
        htmlRendererRef.current,
        rendererProps,
        imageOptimizer,
      );
      renderer.current.getFonts(fontFamilies);
      onResize();
      renderer.current.start();
      rendererProps.renderer.startFunction();
      visualStoryStore.current.start(mainContainerRef.current);
      playablePresentationRef.current = playablePresentation;
      lastAppliedPostRef.current = post;
      initializedId.current = id;

      // Plan-N2 Phase 2 v6 Diagnose-Bridge (2026-05-25 Bene): Live-DOM-
      // Inspect-Hook MIT Auto-Logger. Aktiv nur wenn `?debug=1` im URL.
      //
      // Befund v5: `presentationRender.layers` existiert nicht — der
      // korrekte Pfad ist `playablePresentation.presentationSerialized.
      // layers` (flat Liste aus PostJson, alle Video-Layer mit from/to-
      // Werten). presentationRender hat nur groupTree/slides/options.
      //
      // Double-Init-Fix: wenn ein vorheriger Logger lebt (z.B. weil
      // useEffect re-run wegen dependency-array-change), stoppen wir
      // ihn bevor ein neuer startet. Verhindert die "doppelt-loggt mit
      // clientHeight:0"-Artefakte.
      if (
        typeof window !== "undefined" &&
        window.location?.search?.includes("debug=1")
      ) {
        // Cleanup vorherigen Logger falls vorhanden (Double-Init-Schutz)
        const prevStop = (
          window as unknown as { __scrollyDebugStop?: () => void }
        ).__scrollyDebugStop;
        if (prevStop) {
          try {
            prevStop();
          } catch {
            // ignore
          }
        }

        const debugRefs = {
          visualStoryStore: visualStoryStore.current,
          slidePlayer: playablePresentation.slidePlayer,
          presentationSerialized: playablePresentation.presentationSerialized,
          presentationRender: playablePresentation.presentationRender,
          playablePresentation,
          renderer: renderer.current,
          scrollEl: viewerRootContainerRef.current,
          heightEl: heightRef.current,
          mainEl: mainContainerRef.current,
        };
        (window as unknown as { __scrollyDebug?: unknown }).__scrollyDebug =
          debugRefs;
        // eslint-disable-next-line no-console
        console.log(
          "[scrolly-debug] bridge ready (v6) — Auto-Logger startet in 500ms",
          { keys: Object.keys(debugRefs) },
        );

        // Layer-Counts initial loggen — JETZT MIT MARKERN
        // (isBackground, playbackMode) damit wir unterscheiden können
        // zwischen intentional Multi-Slide (Background/Loop/Scroll-Mode)
        // und kaputten PPI-Lifetimes.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const allLayers =
          (debugRefs.presentationSerialized as any)?.layers ?? [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const initialVideoLayers = allLayers.filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (l: any) => l?.type === "video",
        );
        // eslint-disable-next-line no-console
        console.log("[scrolly-debug] layer-counts", {
          allLayersTotal: allLayers.length,
          videoLayersTotal: initialVideoLayers.length,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          videoLayersList: initialVideoLayers.map((l: any) => ({
            id: typeof l.id === "string" ? l.id.slice(0, 16) : "?",
            from: l.from,
            to: l.to,
            slideId: l.slideId,
            isBackground: l.isBackground === true,
            playbackMode: l.content?.playbackMode ?? "loop(default)",
            loop: l.content?.loop,
            muted: l.content?.muted,
            transparent: l.content?.transparent,
          })),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          slides: (debugRefs.presentationSerialized as any)?.slides?.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (s: any) => ({
              id: typeof s.id === "string" ? s.id.slice(0, 16) : "?",
              from: s.from,
              to: s.to,
            }),
          ),
        });

        const startTime = Date.now();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const intervalId = setInterval(() => {
          if (Date.now() - startTime > 60_000) {
            clearInterval(intervalId);
            // eslint-disable-next-line no-console
            console.log("[scrolly-debug] auto-logger stopped (60s)");
            return;
          }
          try {
            const sc = debugRefs.scrollEl;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const store = debugRefs.visualStoryStore as any;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sp = debugRefs.slidePlayer as any;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const layers =
              (debugRefs.presentationSerialized as any)?.layers ?? [];
            const timer = sp?.timer;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const videos = layers
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .filter((l: any) => l?.type === "video")
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((l: any) => ({
                id: typeof l.id === "string" ? l.id.slice(0, 12) : "?",
                from: l.from,
                to: l.to,
                isBackground: l.isBackground === true,
                playbackMode: l.content?.playbackMode ?? "loop(default)",
                inLifetime:
                  typeof timer === "number" &&
                  l.from <= timer &&
                  l.to >= timer,
              }));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const inLife = videos.filter((v: any) => v.inLifetime);

            // Plan-N2 Block 3 v9: Slide-Gate-Diagnose — zeige
            // currentSlide-Index vs layerOwnSlide-Index pro Video.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const presSlides =
              (debugRefs.presentationRender as any)?.slides ?? [];
            const currentSlide = sp?.currentSlide;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const slideGateInfo = inLife.map((v: any) => {
              let ownSlideIdx = -1;
              for (let i = 0; i < presSlides.length; i++) {
                const start = presSlides[i].from;
                const end =
                  i + 1 < presSlides.length
                    ? presSlides[i + 1].from
                    : Infinity;
                if (start <= v.from && v.from < end) {
                  ownSlideIdx = i;
                  break;
                }
              }
              return {
                videoId: v.id,
                videoFrom: v.from,
                ownSlideIdx,
                currentSlide,
                isOnOwnSlide: ownSlideIdx === currentSlide,
              };
            });

            // eslint-disable-next-line no-console
            console.log("[scrolly-debug]", {
              clientHeight: sc?.clientHeight,
              scrollTop_DOM: sc?.scrollTop,
              scrollTop_store: store?.scrollTop,
              timer,
              currentSlide,
              slidesCount: presSlides.length,
              videosInLifetime: inLife,
              inLifetimeCount: inLife.length,
              slideGateInfo,
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[scrolly-debug] logger error:", e);
          }
        }, 500);

        // Expose stop-function on window für manuelles Stop + Double-Init-
        // Cleanup beim nächsten Mount.
        (
          window as unknown as { __scrollyDebugStop?: () => void }
        ).__scrollyDebugStop = () => {
          clearInterval(intervalId);
          // eslint-disable-next-line no-console
          console.log("[scrolly-debug] stopped");
        };
      }
    } else if (
      initializedId.current !== null &&
      initializedId.current !== id
    ) {
      stopViewer();
      if (!playablePresentationRef.current) {
        const rendererStore = new RendererStore((newProgress) =>
          setProgress(newProgress),
        );
        const playablePresentation = new PlayablePresentation(
          post.data.data,
          rendererStore,
        );
        playablePresentation.slidePlayer.setOnSlideUpdate(
          setCurrentSlideIndex,
        );
        playablePresentationRef.current = playablePresentation;
      } else {
        playablePresentationRef.current.setPresentation(post.data.data);
        playablePresentationRef.current.updateSlideTimings(
          playablePresentationRef.current.presentationRender.slides,
        );
      }

      const rendererProps: RendererProps = {
        isProduction: true,
        stop: stopCallback,
        start: (animate) => {
          playablePresentationRef.current!.slidePlayer.startLoop(animate);
        },
        renderer: playablePresentationRef.current!.renderer,
        slidePlayer: playablePresentationRef.current!.slidePlayer,
        presentation: playablePresentationRef.current!.presentationRender,
        isCoverActive,
        maxTextureWidth: options?.maxTextureWidth,
        editorMode: options?.proEditor,
      };

      if (
        visualStoryStore.current === null &&
        viewerRootContainerRef.current &&
        heightRef.current
      ) {
        visualStoryStore.current = new VisualStoryStore(
          viewerRootContainerRef.current,
          heightRef.current,
          bridgedEvent,
          options?.scrollSource ?? "container",
          options?.proEditor,
          // Template-Editor-Vorschau: kein Per-Breite-Hoehen-Cache (Ratio-Wechsel).
          options?.editorPreview,
        );
      }

      visualStoryStore.current!.setPost(playablePresentationRef.current);
      renderer.current = new Renderer(
        canvasContainerRef.current!,
        htmlRendererRef.current!,
        rendererProps,
        imageOptimizer,
      );

      onResize();
      renderer.current.start();
      rendererProps.renderer.startFunction();
      visualStoryStore.current!.start(mainContainerRef.current!);
      // Reset scroll position to start screen
      visualStoryStore.current!.viewerFrame.scrollTop = 0;
      visualStoryStore.current!.scrollTop = 0;

      initializedId.current = id;
      lastAppliedPostRef.current = post;
      setStarted(false);
    } else if (
      renderer.current &&
      playablePresentationRef.current &&
      initializedId.current === id &&
      // DOPPEL-BOOT-WAECHTER (Jans "Blitzer wie ein schneller Reload",
      // 02.09.2026, per A/B auf main-k8s als ALT-Fehler belegt): Dieser
      // Zweig lief bei JEDER Dependency-Aenderung des Effekts — und
      // `fontFamilies` aendert sich garantiert einmal mitten im Boot,
      // sobald die Team-Schriften fertig geladen sind. setPresentation+
      // setPost setzten dann den KOMPLETTEN Layer-Baum neu auf (zweites
      // "finished loading", sichtbarer Voll-Reload direkt nach dem ersten
      // Boot; mutmasslich auch Benedikts altes "warm langsamer als kalt").
      // Neu setzen nur, wenn sich der POST wirklich geaendert hat — fuer
      // Live-Edits gibt es zusaetzlich den bewachten Effekt weiter unten.
      lastAppliedPostRef.current !== post
    ) {
      lastAppliedPostRef.current = post;
      playablePresentationRef.current.setPresentation(post.data.data);
      playablePresentationRef.current.updateSlideTimings(
        playablePresentationRef.current.presentationRender.slides,
      );
      visualStoryStore.current?.setPost(playablePresentationRef.current);
      // RENDERER AUF DEN NEUEN BAUM UMZEIGEN (Benedikts Template-Editor-Fund
      // 03.09.2026, „Video im Bild-Slot wird nach dem Upload nie sichtbar"):
      // setPresentation ersetzt `presentationRender` auf der Playable-
      // Presentation, der Renderer hielt aber weiter das ALTE Objekt in
      // `props.presentation`. Der Live-Edit-Effekt weiter unten haette
      // umgezeigt, kam aber nie dran — dieser Zweig setzt vorher
      // lastAppliedPostRef, und der Effekt bricht dann sofort ab. Folge im
      // Editor: die Bildschleife las den Slot aus dem alten Baum als BILD,
      // rief pro Frame updateImage und ueberschrieb das laengst geladene,
      // spielende Video-Mesh (HUD: rs=4 SPIELT VIDEO) mit dem Bild. Erst ein
      // Panel-Wechsel (setProps im Cover-Sync) zeigte den neuen Baum.
      (renderer.current.props as unknown as { presentation: unknown }).presentation =
        playablePresentationRef.current.presentationRender;
      // Gelöschte Ebenen abhängen — Begründung im Live-Edit-Effekt unten.
      const aufbau = baumAufbau(playablePresentationRef.current);
      if (aufbau !== baumAufbauRef.current) {
        baumAufbauRef.current = aufbau;
        (
          renderer.current as unknown as { invalidateTree?: () => void }
        ).invalidateTree?.();
      }
      renderer.current.getJson(post);
      // (Jans Pro-Editor-Fund 03.09.2026, "neu Platziertes erscheint nicht":
      // dieselbe Wurzel wie Benedikts Fund oben — die Umzeige-Zeile dort
      // deckt beide Faelle; #623 und #625 hatten sie doppelt eingebaut.)
    }
  }, [
    imgproxyUrl,
    fontFamilies,
    heightRef,
    id,
    isCoverActive,
    onResize,
    post.data.data,
    setCurrentSlideIndex,
    setProgress,
    setStarted,
    stopCallback,
    stopViewer,
    bridgedEvent,
    viewerRootContainerRef,
  ]);

  // Keep renderer cover gate in sync when the editor switches panels.
  useEffect(() => {
    const presentation = playablePresentationRef.current;
    if (!renderer.current || !presentation) return;
    renderer.current.setProps({
      isProduction: true,
      stop: stopCallback,
      start: (animate) => {
        presentation.slidePlayer.startLoop(animate);
      },
      renderer: presentation.renderer,
      slidePlayer: presentation.slidePlayer,
      presentation: presentation.presentationRender,
      isCoverActive,
      // NICHT vergessen (Jans Audio-Jagd 09.08.2026): dieser Sync feuert
      // direkt nach dem Mount und ERSETZT die Props komplett — ohne diese
      // beiden Felder wischte er editorMode (Instanz-Sperre! Rückfall auf
      // die globale Editor-Flagge → Reader-Audio im Pro-Editor-Dialog tot,
      // Diagnose: rendererEditorMode undefined) und maxTextureWidth
      // (Kachel-Texturdeckel) wieder weg.
      maxTextureWidth: options?.maxTextureWidth,
      editorMode: options?.proEditor,
    });
  }, [isCoverActive, options?.sentenceBloomCoverPreview, stopCallback]);

  // EDITOR live-edit: rebuild the render presentation when the `post` CONTENT
  // changes (a scrolly edit hands us a new post identity). The mount effect above
  // builds the scene ONCE (guarded by `renderer.current === null`) — the reader
  // assumption that a scrolly is static. In the editor the content mutates, so we
  // re-derive `presentationRender` (the render tree) via `setPresentation` and
  // re-point the live renderer at the new tree. The renderer's per-layer
  // `contentChange` check then reloads ONLY the changed layers (new image/video
  // texture) — unchanged layers keep their meshes, so no full-scene flash. Skips
  // the initial post (already applied on mount) and never fires for a reader.
  useEffect(() => {
    const pp = playablePresentationRef.current;
    const r = renderer.current;
    if (!pp || !r) return;
    if (lastAppliedPostRef.current === post) return;
    lastAppliedPostRef.current = post;
    pp.setPresentation(post.data.data);
    // The renderer holds `props.presentation` = the OLD presentationRender object;
    // setPresentation replaced it on `pp`, so re-point the renderer at the new one.
    (r.props as unknown as { presentation: unknown }).presentation =
      pp.presentationRender;
    // GELÖSCHTE EBENEN ABHÄNGEN (Jans Fund 12.10.2026, „Assets, die ich
    // lösche, werden im Preview nicht gelöscht"): Der Leichen-Sweep des
    // Renderers läuft nur bei isProduction=false — die Editor-Vorschau
    // nutzt aber den Reader-Pfad (isProduction=true, „Layer ändern sich
    // nie"). invalidateTree ist billig: Wurzel leeren, Meshes leben in
    // layerMeshes weiter und hängen sich im nächsten Bild wieder ein —
    // nur die Ebenen, die es nicht mehr gibt, bleiben draußen.
    // NUR BEI GEÄNDERTEM AUFBAU (Jan 24.10.2026: „Anfasser buggy“, Text hing
    // beim Ziehen hinterher): Jede Mausbewegung am Anfasser schreibt die
    // Position — der Neubau lief 39× in 1,5 s, in 46 von 50 Bildern gab es
    // den Drehpunkt-Knoten der Ebene gar nicht, der Anfasser zog einen
    // abgehängten. Reine Wertänderungen (Position, Farbe, Regler) brauchen
    // keinen Neubau — nur neue/gelöschte/umgehängte/umsortierte Ebenen.
    const aufbau = baumAufbau(pp);
    if (aufbau === baumAufbauRef.current) return;
    baumAufbauRef.current = aufbau;
    (r as unknown as { invalidateTree?: () => void }).invalidateTree?.();
  }, [post]);

  useResizeObserver(viewerRootContainerRef, () => onResize());

  return {
    renderer,
    visualStoryStore,
    mainContainerRef,
    canvasContainerRef,
    htmlRendererRef,
    viewerRootContainerRef,
    onResize,
  };
}
